import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table'
import { ArrowUpDown, ArrowUp, ArrowDown, Search, X, Filter } from 'lucide-react'
import type { Point, PointFilters } from '../types'

const columnHelper = createColumnHelper<Point>()

function formatDate(dateStr: string): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface LocalFilters {
  search: string
  transaction_type: string
  is_valid: string
  date_from: string
  date_to: string
  amount_min: string
  amount_max: string
  points_min: string
  points_max: string
}

function filtersToLocal(filters: PointFilters): LocalFilters {
  return {
    search: filters.search ?? '',
    transaction_type: filters.transaction_type ?? '',
    is_valid: filters.is_valid === undefined ? '' : String(filters.is_valid),
    date_from: filters.date_from ?? '',
    date_to: filters.date_to ?? '',
    amount_min: filters.amount_min !== undefined ? String(filters.amount_min) : '',
    amount_max: filters.amount_max !== undefined ? String(filters.amount_max) : '',
    points_min: filters.points_min !== undefined ? String(filters.points_min) : '',
    points_max: filters.points_max !== undefined ? String(filters.points_max) : '',
  }
}

interface PointsTableProps {
  data: Point[]
  total: number
  filters: PointFilters
  hasActiveFilters: boolean
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onApplyFilters: (filters: Partial<PointFilters>) => void
  onSortingChange: (sortBy?: string, sortOrder?: 'asc' | 'desc') => void
  onClearFilters: () => void
  isLoading: boolean
}

export function PointsTable({
  data,
  total,
  filters,
  hasActiveFilters,
  onPageChange,
  onLimitChange,
  onApplyFilters,
  onSortingChange,
  onClearFilters,
  isLoading,
}: PointsTableProps) {
  const [localFilters, setLocalFilters] = useState<LocalFilters>(() => filtersToLocal(filters))
  const [showFilters, setShowFilters] = useState(false)

  // Sync local state when applied filters change externally (e.g. clearFilters)
  useEffect(() => {
    setLocalFilters(filtersToLocal(filters))
  }, [filters])

  const handleSearch = () => {
    onApplyFilters({
      search: localFilters.search.trim() || undefined,
    })
  }

  const handleApplyPanel = () => {
    onApplyFilters({
      search: localFilters.search.trim() || undefined,
      transaction_type: (localFilters.transaction_type || undefined) as 'add' | 'spend' | undefined,
      is_valid: localFilters.is_valid === '' ? undefined : localFilters.is_valid === 'true',
      date_from: localFilters.date_from || undefined,
      date_to: localFilters.date_to || undefined,
      amount_min: localFilters.amount_min ? Number(localFilters.amount_min) : undefined,
      amount_max: localFilters.amount_max ? Number(localFilters.amount_max) : undefined,
      points_min: localFilters.points_min ? Number(localFilters.points_min) : undefined,
      points_max: localFilters.points_max ? Number(localFilters.points_max) : undefined,
    })
  }

  const handleClear = () => {
    onClearFilters()
  }

  const handleSort = (columnId: string) => {
    if (filters.sort_by === columnId) {
      if (filters.sort_order === 'desc') {
        onSortingChange(columnId, 'asc')
      } else {
        onSortingChange(undefined, undefined)
      }
    } else {
      onSortingChange(columnId, 'desc')
    }
  }

  function SortIcon({ columnId }: { columnId: string }) {
    if (filters.sort_by !== columnId) return <ArrowUpDown size={14} className="text-gray-400" />
    if (filters.sort_order === 'asc') return <ArrowUp size={14} className="text-primary-600" />
    return <ArrowDown size={14} className="text-primary-600" />
  }

  const columns = [
    columnHelper.accessor('id', {
      header: () => (
        <button onClick={() => handleSort('id')} className="flex items-center gap-1 hover:text-primary-600">
          ID <SortIcon columnId="id" />
        </button>
      ),
      cell: ({ getValue }) => (
        <Link
          to={`/points/${getValue()}`}
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          {getValue()}
        </Link>
      ),
      size: 60,
    }),
    columnHelper.accessor('transaction_type', {
      header: () => (
        <button onClick={() => handleSort('transaction_type')} className="flex items-center gap-1 hover:text-primary-600">
          Tipo <SortIcon columnId="transaction_type" />
        </button>
      ),
      cell: ({ getValue }) => {
        const type = getValue()
        return (
          <span
            className={`inline-block px-2 py-0.5 rounded-lg text-xs font-medium ${
              type === 'add'
                ? 'bg-sky-300 text-gray-900'
                : 'bg-red-300 text-gray-900'
            }`}
          >
            {type === 'add' ? 'Acumulación' : 'Canje'}
          </span>
        )
      },
    }),
    columnHelper.accessor('points', {
      header: () => (
        <button onClick={() => handleSort('points')} className="flex items-center gap-1 hover:text-primary-600">
          Puntos <SortIcon columnId="points" />
        </button>
      ),
      cell: ({ row }) => {
        const { points, transaction_type } = row.original
        return transaction_type === 'add'
          ? `${points} acumulados`
          : `${points} canjeados`
      },
    }),
    columnHelper.accessor('used_points', {
      header: () => (
        <button onClick={() => handleSort('used_points')} className="flex items-center gap-1 hover:text-primary-600">
          Usados <SortIcon columnId="used_points" />
        </button>
      ),
      cell: ({ getValue, row }) => {
        if (row.original.transaction_type === 'spend') return '-'
        return getValue()
      },
    }),
    columnHelper.display({
      id: 'remaining',
      header: 'Restantes',
      cell: ({ row }) => {
        if (row.original.transaction_type === 'spend') return '-'
        return row.original.points - row.original.used_points
      },
    }),
    columnHelper.accessor('amount', {
      header: () => (
        <button onClick={() => handleSort('amount')} className="flex items-center gap-1 hover:text-primary-600">
          Monto <SortIcon columnId="amount" />
        </button>
      ),
      cell: ({ getValue }) => `$${getValue().toLocaleString('es-CL')}`,
    }),
    columnHelper.accessor('datetime_expiration', {
      header: () => (
        <button onClick={() => handleSort('datetime_expiration')} className="flex items-center gap-1 hover:text-primary-600">
          Expiración <SortIcon columnId="datetime_expiration" />
        </button>
      ),
      cell: ({ getValue, row }) => {
        if (row.original.transaction_type === 'spend') return '-'
        return formatDate(getValue())
      },
    }),
    columnHelper.accessor('comment', {
      header: 'Comentarios',
      cell: ({ getValue }) => getValue() || '-',
    }),
    columnHelper.accessor('datetime_transaction', {
      header: () => (
        <button onClick={() => handleSort('datetime_transaction')} className="flex items-center gap-1 hover:text-primary-600">
          Fecha <SortIcon columnId="datetime_transaction" />
        </button>
      ),
      cell: ({ getValue }) => formatDateTime(getValue()),
    }),
  ]

  const pagination: PaginationState = {
    pageIndex: filters.page - 1,
    pageSize: filters.limit,
  }

  const pageCount = Math.ceil(total / filters.limit)

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: { pagination },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater
      if (newPagination.pageSize !== filters.limit) {
        onLimitChange(newPagination.pageSize)
      }
      if (newPagination.pageIndex + 1 !== filters.page) {
        onPageChange(newPagination.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  if (isLoading) {
    return <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }

  return (
    <div>
      {/* Search + Filter Toggle */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={localFilters.search}
              onChange={(e) => setLocalFilters((prev) => ({ ...prev, search: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
              placeholder="Buscar por ID o comentario..."
              className="input pl-9"
            />
          </div>
          <button onClick={handleSearch} className="btn btn-primary">
            Buscar
          </button>
          {localFilters.search && (
            <button
              onClick={() => {
                setLocalFilters((prev) => ({ ...prev, search: '' }))
                onApplyFilters({ search: undefined })
              }}
              className="btn btn-secondary"
            >
              <X size={16} />
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn ${showFilters || hasActiveFilters ? 'btn-primary' : 'btn-secondary'} flex items-center gap-1.5`}
          >
            <Filter size={16} />
            Filtros
            {hasActiveFilters && (
              <span className="bg-white text-primary-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold ml-1">
                !
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="p-4 border-b bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
              <select
                value={localFilters.transaction_type}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, transaction_type: e.target.value }))}
                className="input text-sm"
              >
                <option value="">Todos</option>
                <option value="add">Acumulación</option>
                <option value="spend">Canje</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Estado</label>
              <select
                value={localFilters.is_valid}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, is_valid: e.target.value }))}
                className="input text-sm"
              >
                <option value="">Todos</option>
                <option value="true">Válidos</option>
                <option value="false">Inválidos</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha desde</label>
              <input
                type="date"
                value={localFilters.date_from}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, date_from: e.target.value }))}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha hasta</label>
              <input
                type="date"
                value={localFilters.date_to}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, date_to: e.target.value }))}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Monto min ($)</label>
              <input
                type="number"
                value={localFilters.amount_min}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, amount_min: e.target.value }))}
                placeholder="0"
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Monto max ($)</label>
              <input
                type="number"
                value={localFilters.amount_max}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, amount_max: e.target.value }))}
                placeholder="Sin límite"
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Puntos min</label>
              <input
                type="number"
                value={localFilters.points_min}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, points_min: e.target.value }))}
                placeholder="0"
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Puntos max</label>
              <input
                type="number"
                value={localFilters.points_max}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, points_max: e.target.value }))}
                placeholder="Sin límite"
                className="input text-sm"
              />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={handleApplyPanel} className="btn btn-primary text-sm">
              Aplicar Filtros
            </button>
            {hasActiveFilters && (
              <button onClick={handleClear} className="btn btn-secondary text-sm">
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-50 border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-4 py-3 font-medium text-gray-700">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-gray-500">
                  No se encontraron transacciones de puntos
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50 transition-colors">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {total} resultados - Página {filters.page} de {pageCount || 1}
          </span>
          <select
            value={filters.limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="input w-auto text-sm"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} por página
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(filters.page - 1)}
            disabled={filters.page <= 1}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => onPageChange(filters.page + 1)}
            disabled={filters.page >= pageCount}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  )
}
